export const normalizeStep = (step) => {
  if (!step) return step;
  return {
    ...step,
    detailCandidateSent: step.detailCandidateSent || step.detail_candidate_sent || null,
    detailCandidateInterview: step.detailCandidateInterview || step.detail_candidate_interview || null,
    detailGoNoGo: step.detailGoNoGo || step.detail_go_no_go || null,
    detailSale: step.detailSale || step.detail_sale || null,
  };
};

export const buildScoringCache = (records) => {
  const byPair = new Map();
  records.forEach(record => {
    if (!record?.candidateId || !record?.needId) return;
    byPair.set(`${record.needId}_${record.candidateId}`, record);
  });

  const scoringData = Array.from(byPair.values());
  const scoringDataByNeed = {};
  const scoringDataByCandidate = {};
  scoringData.forEach(record => {
    (scoringDataByNeed[record.needId] ||= []).push(record);
    (scoringDataByCandidate[record.candidateId] ||= []).push(record);
  });
  return { scoringData, scoringDataByNeed, scoringDataByCandidate };
};

export const withoutContextualScoring = (candidate) => {
  const normalized = { ...candidate };
  [
    'relevanceScore',
    'relevance_score',
    'scoreJustification',
    'score_justification',
    'scoreJobId',
    'score_job_id',
    'scoredAt',
    'scored_at',
  ].forEach(field => delete normalized[field]);
  return normalized;
};

export const applyCandidateEvent = (cache, message) => {
  const payload = message.payload || message.data || {};
  const candidateId = message.entity_id || payload.id;
  if (!candidateId) return cache;

  if (message.event_type === 'candidate_deleted' || message.event_type === 'candidate_merged') {
    const removedId = message.event_type === 'candidate_deleted' ? candidateId : payload.merged_from;
    if (!removedId) return cache;
    const candidatesById = { ...cache.candidatesById };
    delete candidatesById[removedId];
    if (message.event_type === 'candidate_merged') {
      candidatesById[candidateId] = { ...candidatesById[candidateId], ...payload, id: candidateId };
    }
    return {
      ...cache,
      candidatesById,
      candidatesByNeed: Object.fromEntries(Object.entries(cache.candidatesByNeed || {}).map(([needId, ids]) => [
        needId, [...new Set((ids || []).flatMap(id => id !== removedId ? [id]
          : message.event_type === 'candidate_merged' ? [candidateId] : []))],
      ])),
      candidateSteps: Object.fromEntries(Object.entries(cache.candidateSteps || {}).filter(([key]) => !key.endsWith(`_${removedId}`))),
    };
  }

  const needId = payload.needId || payload.need_id;
  const action = payload.action?.toUpperCase();
  const candidatePatch = Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => (
      !['action', 'needId', 'need_id'].includes(key) && value !== undefined
    )),
  );
  const existingCandidate = cache.candidatesById?.[candidateId];
  const shouldStoreCandidate = existingCandidate || Object.keys(candidatePatch).length > 1;
  const candidatesById = shouldStoreCandidate
    ? {
        ...(cache.candidatesById || {}),
        [candidateId]: { ...(existingCandidate || {}), ...candidatePatch, id: candidateId },
      }
    : cache.candidatesById;
  let candidatesByNeed = cache.candidatesByNeed || {};

  if (needId && Object.prototype.hasOwnProperty.call(candidatesByNeed, needId)) {
    const currentIds = candidatesByNeed[needId] || [];
    const nextIds = action === 'DETACHED'
      ? currentIds.filter(id => id !== candidateId)
      : Array.from(new Set([...currentIds, candidateId]));
    candidatesByNeed = { ...candidatesByNeed, [needId]: nextIds };
  }

  const candidateSteps = action === 'DETACHED' && needId
    ? Object.fromEntries(
        Object.entries(cache.candidateSteps || {}).filter(([key]) => key !== `${needId}_${candidateId}`),
      )
    : cache.candidateSteps;

  return { ...cache, candidatesById, candidatesByNeed, candidateSteps };
};

export const applyCandidateSnapshot = (cache, needId, candidates, requestGeneration, currentGeneration) => {
  if (requestGeneration !== currentGeneration) return cache;
  const ids = candidates.map(candidate => candidate.id);
  const candidatesById = candidates.reduce(
    (byId, candidate) => ({ ...byId, [candidate.id]: withoutContextualScoring(candidate) }),
    { ...(cache.candidatesById || {}) },
  );
  return {
    ...cache,
    candidatesById,
    candidatesByNeed: { ...(cache.candidatesByNeed || {}), [needId]: ids },
  };
};

export const applyStepSnapshot = (cache, key, steps, requestGeneration, currentGeneration) => {
  if (requestGeneration !== currentGeneration) return cache;
  return {
    ...cache,
    candidateSteps: {
      ...(cache.candidateSteps || {}),
      [key]: steps.map(normalizeStep),
    },
  };
};

export const applyStepEvent = (cache, message) => {
  const payload = message.payload || message.data || {};
  const needId = payload.needId || payload.need_id;
  const candidateId = payload.candidateId || payload.candidate_id;
  const stepId = payload.stepId || payload.step_id || payload.id || message.entity_id;
  if (!needId || !candidateId || !stepId) return cache;

  const key = `${needId}_${candidateId}`;
  const existingSteps = cache.candidateSteps?.[key] || [];
  if (message.event_type === 'step_deleted') {
    return {
      ...cache,
      candidateSteps: {
        ...(cache.candidateSteps || {}),
        [key]: existingSteps.filter(step => step.id !== stepId),
      },
    };
  }

  const index = existingSteps.findIndex(step => step.id === stepId);
  if (index === -1 && !payload.type) return cache;
  const existingStep = index === -1 ? {} : existingSteps[index];
  const updatedStep = normalizeStep({
    ...existingStep,
    ...payload,
    id: stepId,
    type: payload.type || existingStep.type,
    needId,
    candidateId,
  });
  const nextSteps = index === -1
    ? [updatedStep, ...existingSteps]
    : existingSteps.map((step, stepIndex) => stepIndex === index ? updatedStep : step);
  return {
    ...cache,
    candidateSteps: { ...(cache.candidateSteps || {}), [key]: nextSteps },
  };
};
